import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { AlertTriangle, Database, Shield, Loader2, Trash2, Eye } from "lucide-react";
import { API } from "../App";

/**
 * Admin → Maintenance tab.
 * Wipe test / demo data while preserving programs, courses, modules, lessons,
 * CMS, admins, and (by default) the admin's own artefacts.
 *
 * Two-step flow:
 *   1. Preview impact (read-only count of what would be deleted)
 *   2. Type "DELETE" + click Wipe button to execute
 */
const MaintenanceTab = () => {
  const [includeAudit, setIncludeAudit] = useState(true);
  const [includeAdminArtefacts, setIncludeAdminArtefacts] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [preview, setPreview] = useState(null);
  const [purging, setPurging] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);

  const buildQS = () => {
    const params = new URLSearchParams();
    params.set("include_audit_logs", String(includeAudit));
    params.set("include_admin_artefacts", String(includeAdminArtefacts));
    return params.toString();
  };

  const fetchPreview = async () => {
    setPreviewing(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/admin/maintenance/purge-preview?${buildQS()}`, { credentials: "include" });
      if (!r.ok) throw new Error("Preview failed");
      setPreview(await r.json());
    } catch (e) {
      toast.error("Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const executePurge = async () => {
    if (confirmPhrase !== "DELETE") {
      toast.error('Type "DELETE" exactly to confirm');
      return;
    }
    const really = window.confirm(
      `LAST WARNING.\n\nYou are about to permanently delete ${preview?.total_to_delete ?? "all"} records from this database (preview env if you're testing, production if deployed).\n\nThis cannot be undone. Continue?`
    );
    if (!really) return;
    setPurging(true);
    try {
      const r = await fetch(`${API}/admin/maintenance/purge-test-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          confirm_phrase: confirmPhrase,
          include_audit_logs: includeAudit,
          include_admin_artefacts: includeAdminArtefacts,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Purge failed");
      setResult(data);
      setPreview(null);
      setConfirmPhrase("");
      toast.success(`Wiped ${data.total_deleted} records 🧹`);
    } catch (e) {
      toast.error(e.message || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="maintenance-tab">
      <Card className="card-surface border-2 border-red-300 bg-red-50/30">
        <CardHeader>
          <CardTitle className="text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Danger Zone — Purge Test / Demo Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="bg-white border border-red-200 rounded-xl p-4 space-y-2">
            <p className="text-sm text-slate-700">
              This tool wipes <strong>everything except</strong>:
            </p>
            <ul className="text-sm text-slate-600 list-disc pl-6 space-y-0.5">
              <li>Admin user accounts (you and any other admin)</li>
              <li>Programs (the 8 career tracks) and their cohort schedules</li>
              <li>Welcome packets</li>
              <li>Courses / Modules / Lessons (learning materials — Item 4 you wanted to keep)</li>
              <li>CMS pages: FAQ, Contact page, Site content, Working Style question catalogue</li>
            </ul>
            <p className="text-xs text-slate-500 pt-2 border-t border-slate-100 mt-2">
              Use this before a demo, a launch, or when you want a clean slate. <strong>Cannot be undone.</strong>
            </p>
          </div>

          {/* Options */}
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Options</p>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <Checkbox checked={includeAudit} onCheckedChange={(v) => setIncludeAudit(!!v)} data-testid="opt-audit" className="mt-0.5" />
              <span>
                <strong>Also wipe audit logs</strong>
                <span className="block text-xs text-slate-500">Clears <code>audit_logs</code> entirely. Recommended for production scrub.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <Checkbox checked={includeAdminArtefacts} onCheckedChange={(v) => setIncludeAdminArtefacts(!!v)} data-testid="opt-admin" className="mt-0.5" />
              <span>
                <strong>Also wipe admin's own artefacts</strong>
                <span className="block text-xs text-slate-500">
                  By default, your admin account keeps its strengths profile, badges, certifications,
                  community posts, etc. Tick this to wipe those too.
                </span>
              </span>
            </label>
          </div>

          {/* Step 1: Preview */}
          <div className="flex flex-wrap items-center gap-3 border-t border-red-200 pt-4">
            <Button onClick={fetchPreview} disabled={previewing} variant="outline" data-testid="purge-preview-btn">
              {previewing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Counting…</> : <><Eye className="w-4 h-4 mr-1.5" /> Preview Impact</>}
            </Button>
            <p className="text-xs text-slate-500">Step 1 — see exactly what would be deleted before committing.</p>
          </div>

          {preview && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="purge-preview-result">
              <p className="text-sm font-bold text-red-700 flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                Would delete {preview.total_to_delete.toLocaleString()} record{preview.total_to_delete === 1 ? "" : "s"}
              </p>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {Object.entries(preview.to_delete).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">{k}</span>
                    <Badge className={v > 0 ? "bg-red-100 text-red-700 border-red-200" : "bg-slate-100 text-slate-400 border-slate-200"}>
                      {v}
                    </Badge>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs font-bold text-emerald-700 flex items-center gap-1.5 mb-2">
                  <Shield className="w-3.5 h-3.5" />
                  Preserved
                </p>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  {Object.entries(preview.to_preserve).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">{k}</span>
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">{v}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Execute */}
          {preview && (
            <div className="bg-white border-2 border-red-300 rounded-xl p-4 space-y-3" data-testid="purge-execute-block">
              <p className="text-sm font-bold text-red-700">Step 2 — Confirm and execute</p>
              <div>
                <Label className="text-slate-700 text-xs">Type <code className="px-1.5 py-0.5 bg-slate-100 rounded text-red-700 font-bold">DELETE</code> (uppercase) to enable the wipe button</Label>
                <Input
                  value={confirmPhrase}
                  onChange={(e) => setConfirmPhrase(e.target.value)}
                  placeholder="DELETE"
                  className="input-base mt-1 font-mono tracking-widest"
                  data-testid="purge-confirm-input"
                />
              </div>
              <Button
                onClick={executePurge}
                disabled={purging || confirmPhrase !== "DELETE"}
                className="w-full bg-red-700 hover:bg-red-800 text-white font-bold"
                data-testid="purge-execute-btn"
              >
                {purging ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Wiping {preview.total_to_delete.toLocaleString()} records…</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-1.5" /> Wipe {preview.total_to_delete.toLocaleString()} Records Permanently</>
                )}
              </Button>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 space-y-2" data-testid="purge-result">
              <p className="text-sm font-bold text-emerald-800 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Done — wiped {result.total_deleted.toLocaleString()} records
              </p>
              <p className="text-xs text-emerald-700">
                Purged at {new Date(result.purged_at).toLocaleString()} by {result.purged_by}
              </p>
              <details className="text-xs text-slate-600">
                <summary className="cursor-pointer hover:text-slate-900">View full breakdown</summary>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-2">
                  {Object.entries(result.deleted).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between">
                      <span>{k}</span>
                      <Badge className="bg-white text-slate-700 border-slate-200">{v}</Badge>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MaintenanceTab;
